import type { IChartData, ISignalDataResponse } from "../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisDataMark } from "../types";
import { resolveDataMarkMeasureDate } from "./data-marks";
import { parseMultiAnalysisChartTime } from "./range-frame";
import {
  getSpectrumAmplitudeFactor,
  isVibrationUnitCode,
  resolvePointFrequencyHz,
  type TVibrationUnitCode,
} from "./signal-unit-conversion";

export const createFrequencyProfilePointKey = (
  idMeasure: number | null | undefined,
  idPoint: number | null | undefined,
  date: string,
): string => `${Number(idMeasure)}:${Number(idPoint)}:${parseMultiAnalysisChartTime(date)}`;

export const convertClickedSpectrumXToHz = (
  nativeX: number,
  unitsXCode: number | null | undefined,
  rotationFrequencyRpm: number | null | undefined,
): number | null => {
  if (!Number.isFinite(nativeX) || nativeX < 0) {
    return null;
  }

  const frequencyHz = resolvePointFrequencyHz(nativeX, unitsXCode, rotationFrequencyRpm);

  if (!Number.isFinite(frequencyHz) || frequencyHz < 0 || (unitsXCode === 4 && frequencyHz <= 0)) {
    return null;
  }

  return frequencyHz;
};

const extractSignalAmplitudeAtNativeX = (signal: ISignalDataResponse, nativeX: number): number | null => {
  const isComplexSpectrum = signal.rangeType === 2;
  const count = isComplexSpectrum ? signal.data.length / 2 : signal.data.length;

  if (count < 2) {
    return null;
  }

  if (!Number.isFinite(nativeX)) {
    return null;
  }

  const span = signal.xEnd - signal.xStart;

  if (!Number.isFinite(span) || span === 0) {
    return null;
  }

  const minX = Math.min(signal.xStart, signal.xEnd);
  const maxX = Math.max(signal.xStart, signal.xEnd);

  if (nativeX < minX || nativeX > maxX) {
    return null;
  }

  const index = Math.round(((nativeX - signal.xStart) / span) * (count - 1));
  const value = signal.data[Math.min(Math.max(index, 0), count - 1)];

  return Number.isFinite(value) ? value : null;
};

export const getUniqueFrequencyProfileMeasureRefs = (
  marks: readonly IMultiAnalysisDataMark[],
): Array<{ idMeasure: number; date: string }> => {
  const seen = new Set<string>();

  return marks.flatMap(mark => {
    const date = resolveDataMarkMeasureDate(mark);
    const key = `${mark.idMeasure}:${date}`;

    if (seen.has(key)) {
      return [];
    }

    seen.add(key);

    return [{ idMeasure: mark.idMeasure, date }];
  });
};

export const applyFrequencyProfileToTrendSeries = (
  series: readonly IChartData[],
  signals: readonly ISignalDataResponse[],
  marks: readonly IMultiAnalysisDataMark[],
  nativeX: number,
  targetUnits: TVibrationUnitCode,
  formatUnitLabel: (unitCode: number) => string,
): IChartData[] => {
  const marksByRef = new Map<string, IMultiAnalysisDataMark>();

  marks.forEach(mark => {
    marksByRef.set(
      createFrequencyProfilePointKey(mark.idMeasure, mark.idPoint, resolveDataMarkMeasureDate(mark)),
      mark,
    );
  });

  const amplitudes = new Map<string, { value: number; unitCode: number }>();

  signals.forEach(signal => {
    const rawValue = extractSignalAmplitudeAtNativeX(signal, nativeX);

    if (rawValue == null) {
      return;
    }

    const unitsXCode = signal.rangeType === 1 || signal.rangeType === 3 ? 3 : 4;
    const frequencyHz = resolvePointFrequencyHz(nativeX, unitsXCode, signal.rotationFrequencyRpm);
    const sourceUnits = signal.units;
    const shouldConvert = isVibrationUnitCode(sourceUnits);
    const value = shouldConvert
      ? rawValue * getSpectrumAmplitudeFactor(sourceUnits, targetUnits, frequencyHz)
      : rawValue;
    const unitCode = shouldConvert ? targetUnits : sourceUnits;
    const mark = marksByRef.get(createFrequencyProfilePointKey(signal.idMeasure, signal.idPoint, signal.date));

    amplitudes.set(createFrequencyProfilePointKey(signal.idMeasure, signal.idPoint, mark?.date ?? signal.date), {
      value,
      unitCode,
    });
  });

  return series
    .map(item => {
      let profileUnitCode: number | null = null;
      const items = item.items.flatMap(point => {
        const amplitude = amplitudes.get(createFrequencyProfilePointKey(item.idMeasure, item.idPoint, point.date));

        if (!amplitude) {
          return [];
        }

        profileUnitCode ??= amplitude.unitCode;
        return [{ ...point, value: amplitude.value }];
      });

      return {
        ...item,
        items,
        ...(profileUnitCode == null ? {} : { unitCode: profileUnitCode, units: formatUnitLabel(profileUnitCode) }),
      };
    })
    .filter(item => item.items.length > 0);
};
